import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, ArrowLeft, Check } from "lucide-react";
import { SITE } from "../../lib/data";

/**
 * /thank-you — dedicated post-submit landing page.
 *
 * Fires Google Ads / GA4 conversion events on page load so the conversion
 * is reliably attributed even on refresh, direct link share, or back-button
 * return. Stops short-circuiting in case the user reaches this page without
 * a fresh form submit (gtag is already debounced server-side anyway).
 */

const WhatsAppIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.04 21.785h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.889-9.884 2.641 0 5.124 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.886 9.884zm8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.49-8.413z"/>
  </svg>
);

export default function ThankYou() {
  const location = useLocation();
  const firstName = (location.state?.name || "").split(" ")[0] || "";

  useEffect(() => {
    // Fire conversion on page load — guarantees attribution even if the
    // form-handler push didn't catch (refresh, back-button, etc.)
    try {
      if (typeof window.gtag === "function") {
        window.gtag("event", "generate_lead", {
          event_category: "lead",
          event_label: "thank-you-page",
          value: 1,
        });
        if (window.AMARJA_ADS_CONVERSION) {
          window.gtag("event", "conversion", {
            send_to: window.AMARJA_ADS_CONVERSION,
            value: 1.0,
            currency: "INR",
          });
        }
      }
    } catch (_e) {
      /* analytics non-critical */
    }
    // Always scroll to top
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div
      data-testid="thank-you-page"
      className="relative min-h-screen bg-[#F7F5F0] text-[#1A362D] flex flex-col"
    >
      {/* Subtle hero image as background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <img
          src="/img/site/drone1.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#F7F5F0]/70 via-[#F7F5F0]/85 to-[#F7F5F0]" />
      </div>

      {/* Top brand bar */}
      <header className="relative z-10 px-6 sm:px-10 lg:px-16 py-6">
        <Link
          to="/"
          data-testid="thank-you-logo-link"
          className="inline-flex items-center"
          aria-label="Amarja Hills home"
        >
          <img
            src={SITE.logo}
            alt="Amarja Hills"
            className="h-12 md:h-14 w-auto logo-on-cream"
          />
        </Link>
      </header>

      {/* Main content card */}
      <main className="relative z-10 flex-1 flex items-center justify-center px-6 py-10 sm:py-16">
        <div className="max-w-2xl w-full bg-white border border-[#1A362D]/10 shadow-[0_20px_60px_rgba(26,54,45,0.08)] px-7 sm:px-12 py-10 sm:py-16">
          <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-[#1A362D] text-[#F7F5F0] flex items-center justify-center mb-7">
            <Check className="w-7 h-7 sm:w-8 sm:h-8" strokeWidth={2.2} />
          </div>

          <h1
            data-testid="thank-you-title"
            className="font-serif-display text-4xl sm:text-5xl md:text-6xl text-[#1A362D] leading-[0.98] tracking-tight font-light"
          >
            Thank you{firstName ? `, ${firstName}` : ""}.
          </h1>

          <p className="mt-6 text-base sm:text-lg text-[#3A453B] leading-relaxed max-w-lg">
            Your enquiry is in. A senior advisor will personally reach out
            within <strong className="text-[#1A362D] font-medium">24 hours</strong> to
            walk you through the master plan, available plots, and pricing.
          </p>

          {/* What happens next */}
          <div className="mt-10 pt-10 border-t border-[#1A362D]/10">
            <p className="text-[10px] sm:text-[11px] tracking-[0.28em] uppercase text-[#987F53] font-semibold mb-6">
              What happens next
            </p>
            <ol className="space-y-5">
              {[
                "You'll receive a call from a senior advisor within 24 hours",
                "We'll walk you through the master plan, plots & pricing",
                "A private site visit is scheduled at your convenience",
              ].map((step, i) => (
                <li
                  key={i}
                  data-testid={`thank-you-step-${i + 1}`}
                  className="flex gap-4 items-start"
                >
                  <span className="font-serif-display text-2xl text-[#987F53] italic font-light leading-none w-7 flex-shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-[#3A453B] text-sm sm:text-base leading-relaxed">
                    {step}
                  </span>
                </li>
              ))}
            </ol>
          </div>

          {/* Direct contact buttons */}
          <div className="mt-10 pt-10 border-t border-[#1A362D]/10">
            <p className="text-sm text-[#4A524D] mb-4">
              Or reach out directly — we&apos;ll happily speak now.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                data-testid="thank-you-whatsapp"
                href={`https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent(
                  `Hello Amarja Hills, I just submitted an enquiry${firstName ? ` (${firstName})` : ""} and would like to continue on WhatsApp.`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#25D366] text-white font-semibold text-[12px] tracking-[0.18em] uppercase hover:bg-[#1ebe5d] transition-colors"
              >
                <WhatsAppIcon className="w-5 h-5" />
                Continue on WhatsApp
              </a>
              <a
                data-testid="thank-you-call"
                href={`tel:${SITE.telHref}`}
                className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#1A362D] text-[#F7F5F0] font-semibold text-[12px] tracking-[0.18em] uppercase hover:bg-[#987F53] transition-colors"
              >
                <Phone className="w-5 h-5" />
                Call {SITE.phone}
              </a>
            </div>
          </div>

          {/* Back link */}
          <div className="mt-10">
            <Link
              data-testid="thank-you-back"
              to="/"
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase font-semibold text-[#1A362D] hover:text-[#987F53] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Amarja Hills
            </Link>
          </div>
        </div>
      </main>

      {/* Subtle footer */}
      <footer className="relative z-10 text-center pb-8 text-[10px] sm:text-xs tracking-[0.22em] uppercase text-[#4A524D]/65">
        © {new Date().getFullYear()} Amarja Hills · Nanak Properties Pvt Ltd
      </footer>
    </div>
  );
}
